<template>
  <!-- Single root for the whole widget — Vue 2's patch algorithm reuses
       the same DOM node when toggling between v-if/v-else top-level
       siblings of the same tag, which means the inner content didn't
       reactively re-render when isWidgetHidden flipped. Folding both
       states into one always-present root with an inner v-if/v-else
       (and an explicit :key on each branch) forces a full unmount +
       mount on toggle. -->
  <div
    ref="widgetRootRef"
    class="comparator-feedback"
    :class="{ 'comparator-feedback--minimized': isWidgetHidden }"
  >
    <!-- Hidden state: collapse the whole widget to a single 3-dots icon
         button that re-exposes the widget on demand. Lets the advisor
         reclaim the toolbar space without permanently losing access to
         the feedback flow — they can always click the dots later. -->
    <template v-if="isWidgetHidden">
      <v-menu key="comparator-feedback-minimized" offset-y left>
        <template #activator="{ on: dotOn, attrs: dotAttrs }">
          <button
            type="button"
            class="comparator-feedback__overflow-btn comparator-feedback__overflow-btn--standalone"
            v-bind="dotAttrs"
            v-on="dotOn"
            aria-label="Opcje feedbacku"
          >
            <v-icon small>mdi-dots-vertical</v-icon>
          </button>
        </template>
        <v-list dense class="comparator-feedback__overflow-menu">
          <v-list-item @click="restoreWidget">
            <v-list-item-icon>
              <v-icon small>mdi-message-text-outline</v-icon>
            </v-list-item-icon>
            <v-list-item-title>Zostaw feedback</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </template>

    <!-- Full widget state: prompt bubble + emoji scale + popover. -->
    <template v-else>
    <!-- Prompt bubble — small "speech bubble" on the left of the scale.
         Stays visible until the user closes it (X), then a localStorage
         flag keeps it hidden across reloads on this browser. The little
         arrow on the right edge points at the emoji scale. -->
    <transition name="comparator-feedback-prompt-fade">
      <div v-if="isPromptVisible" class="comparator-feedback__prompt">
        <span class="comparator-feedback__prompt-text">Jak oceniasz porównywarkę?</span>
        <button
          type="button"
          class="comparator-feedback__prompt-close"
          aria-label="Zamknij"
          @click="dismissPrompt"
        >
          <v-icon small>mdi-close</v-icon>
        </button>
      </div>
    </transition>

    <!-- Trigger: emoji scale OR collapsed selected emoji.
         A v-menu wraps the trigger so the popover anchors to whichever emoji
         is currently visible. activator="parent" is set via the slot so the
         menu finds the right element regardless of which branch renders. -->
    <!-- close-on-click=false: Vuetify's outside-click detector runs in the
         document's capture phase, before any @click.stop on our emoji
         buttons gets a chance to halt propagation. With it on, switching
         between emojis while the popover is open registers as an outside
         click (target outside the menu content, briefly outside the
         activator during Vue's reactive flush) and snaps the menu closed
         — then our nextTick + isMenuOpen=true reopens it, producing a
         visible flicker. We close the menu only via Cancel / Submit /
         Esc (Vuetify still handles Esc natively). -->
    <v-menu
      v-model="isMenuOpen"
      :close-on-content-click="false"
      :close-on-click="false"
      offset-y
      max-width="400"
      min-width="340"
      content-class="comparator-feedback__menu"
      transition="slide-y-transition"
    >
      <template #activator="{ attrs }">
        <!-- We drive `isMenuOpen` via v-model directly from our click
             handlers, so we only consume `attrs` here (a11y bookkeeping)
             and skip Vuetify's `on` handlers — they'd otherwise open the
             menu on every emoji click before our state update lands.
             Hover handlers re-expand the scale after submission so the
             user can re-rate without first clicking the collapsed pill. -->
        <!-- The trigger now hosts the pill background + border itself
             (was on .__scale before) so the 3-dots overflow button can
             live inside the same pill as the emoji scale. Both the
             expanded scale and the collapsed single-emoji button render
             as transparent inner sections of this pill. -->
        <div
          class="comparator-feedback__trigger"
          :class="{ 'comparator-feedback__trigger--menu-open': isMenuOpen }"
          v-bind="attrs"
          @mouseenter="onTriggerEnter"
          @mouseleave="onTriggerLeave"
        >
          <!-- Single always-rendered scale. Non-selected emojis collapse
               to width: 0 + opacity: 0 (via `--hidden`) when the widget
               is in post-submit collapsed mode, then animate back to
               full size on hover. Animating the v-if-toggled DOM swap
               wouldn't work because Vue unmounts the entire collapsed
               <button> and remounts five emoji buttons — no transition
               anchor between the two trees. -->
          <div class="comparator-feedback__scale" @click.stop>
            <!-- .stop on the click stops v-menu's outside-click detector
                 from treating one emoji click as "click outside the
                 currently-open popover" (which would close it and force a
                 re-open + re-measure on the next emoji click — causing the
                 popover to flicker and re-anchor). With .stop, switching
                 between emojis while the popover is open just updates the
                 highlight and the popover's question text in place. -->
            <button
              v-for="option in RATING_OPTIONS"
              :key="option.value"
              type="button"
              class="comparator-feedback__emoji"
              :class="{
                'comparator-feedback__emoji--selected': selectedRating === option.value,
                'comparator-feedback__emoji--hidden': isInCollapsedMode && selectedRating !== option.value,
              }"
              :data-tooltip="option.label"
              :aria-label="option.label"
              @click.stop="onRatingClick(option.value)"
            >
              <!-- Clip wrapper around the glyph: when the button shrinks
                   to width: 0 in `--hidden` state, the wrapper's
                   overflow: hidden hides the glyph that would otherwise
                   stick out to the right. Keeping overflow on a CHILD
                   (not on the button itself) means the button can still
                   render its ::after tooltip outside its own bounds. -->
              <span class="comparator-feedback__emoji-inner">
                <span class="comparator-feedback__emoji-glyph">{{ option.emoji }}</span>
              </span>
            </button>
          </div>

          <!-- Divider between the scale/collapsed and the 3-dots. Visual
               anchor so the overflow button doesn't read as "just another
               emoji slot". -->
          <div class="comparator-feedback__trigger-divider" aria-hidden="true" />

          <!-- 3-dots overflow menu, anchored to this button inside the
               pill. @click.stop prevents the click from reaching v-menu's
               (main popover) outside-click detector or our scale-level
               @click.stop wrapper, but v-on="dotOn" still wires the
               overflow menu's own open handler. -->
          <v-menu offset-y left>
            <template #activator="{ on: dotOn, attrs: dotAttrs }">
              <button
                type="button"
                class="comparator-feedback__overflow-btn"
                v-bind="dotAttrs"
                v-on="dotOn"
                aria-label="Więcej opcji"
                @click.stop
              >
                <v-icon small>mdi-dots-vertical</v-icon>
              </button>
            </template>
            <v-list dense class="comparator-feedback__overflow-menu">
              <v-list-item @click="dismissWidget">
                <v-list-item-icon>
                  <v-icon small>mdi-eye-off-outline</v-icon>
                </v-list-item-icon>
                <v-list-item-title>Ukryj widget</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>
      </template>

      <div class="comparator-feedback__popover">
        <!-- Submitted state: "Dziękujemy!" then auto-resets to expanded
             scale so the advisor can rate again. The auto-close timer
             pauses while the cursor is hovering the popover (so the
             user can read it / click "Ukryj widget" without it sliding
             out from under them). -->
        <template v-if="showThanks">
          <div
            class="comparator-feedback__thanks"
            @mouseenter="onThanksMouseEnter"
            @mouseleave="onThanksMouseLeave"
          >
            <div class="comparator-feedback__thanks-row">
              <!-- The chosen emoji with a small green check overlay —
                   replaces the plain mdi-check-circle so the popover
                   acknowledges *which* rating just went through, not
                   just that "something" was submitted. -->
              <div class="comparator-feedback__thanks-emoji" aria-hidden="true">
                <span class="comparator-feedback__thanks-emoji-glyph">{{ selectedOption ? selectedOption.emoji : '' }}</span>
                <span class="comparator-feedback__thanks-emoji-check">
                  <v-icon size="16" color="white">mdi-check</v-icon>
                </span>
              </div>
              <div class="comparator-feedback__thanks-text">
                <strong class="comparator-feedback__thanks-heading">Dziękujemy za opinię!</strong>
                <span class="comparator-feedback__thanks-caption">Twój feedback pomaga nam ulepszać porównywarkę.</span>
              </div>
              <button
                type="button"
                class="comparator-feedback__thanks-close"
                aria-label="Zamknij"
                @click="closeThanksNow"
              >
                <v-icon small>mdi-close</v-icon>
              </button>
            </div>
            <div class="comparator-feedback__thanks-actions">
              <!-- fp-btn (design-system button). Default flags = auto-
                   outlined; `small` + an explicit height keeps it the
                   compact secondary action it is, while still matching
                   the toolkit's other small buttons elsewhere in the
                   calculator. -->
              <fp-btn small height="36px" @click="dismissWidgetFromThanks">
                <v-icon small class="mr-1">mdi-eye-off-outline</v-icon>
                Ukryj widget
              </fp-btn>
            </div>
          </div>
        </template>

        <template v-else>
          <div class="comparator-feedback__popover-header">
            <span class="comparator-feedback__popover-emoji">{{ selectedOption ? selectedOption.emoji : '' }}</span>
            <div class="comparator-feedback__popover-header-text">
              <!-- Overscript: the rating's short label ("Bardzo źle"…"Świetnie")
                   echoed above the question. Lets the user double-check the
                   rating they picked without leaving the popover. -->
              <span class="comparator-feedback__popover-overscript">{{ selectedOption ? selectedOption.label : '' }}</span>
              <strong class="comparator-feedback__popover-question">{{ popoverQuestion }}</strong>
            </div>
          </div>

          <!-- Wrap the textarea in a plain div so the bottom-spacer is on
               our own element, not on Vuetify's v-input root. Vuetify's
               v-text-field has its own internal padding/margin rules that
               were swallowing the margin-bottom set directly on the
               component. -->
          <div class="comparator-feedback__textarea-wrap">
            <v-textarea
              ref="textareaRef"
              v-model="comment"
              rows="3"
              auto-grow
              no-resize
              outlined
              dense
              hide-details
              class="comparator-feedback__textarea"
            />
          </div>

          <!-- Loading state while modern-screenshot is rendering. Shown
               before screenshotDataUrl is set so the user gets a visible
               "something is happening" cue rather than staring at an
               empty popover for a few hundred ms after their drag. -->
          <div
            v-if="isCapturingScreenshot && !screenshotDataUrl"
            class="comparator-feedback__attachment comparator-feedback__attachment--loading"
          >
            <!-- Plain CSS spinner instead of Vuetify's v-progress-circular
                 — the latter's SVG paths render unreliably at small sizes
                 (looked like a static arc on size: 20 / width: 2). This
                 is fully CSS-animated so prefers-reduced-motion is the
                 only thing that can stop it. -->
            <span class="comparator-feedback__spinner" aria-hidden="true" />
            <div class="comparator-feedback__attachment-meta">
              <span>Zapisuję screenshot…</span>
            </div>
          </div>

          <!-- Screenshot preview + remove (visible once capture finishes
               or after a file upload). Label differs by source so the
               user sees whether the attachment came from a drag-rect on
               the page or a file they picked from disk. -->
          <div
            v-else-if="screenshotDataUrl"
            class="comparator-feedback__attachment"
          >
            <img :src="screenshotDataUrl" class="comparator-feedback__attachment-thumb" alt="Załączony screenshot" />
            <div class="comparator-feedback__attachment-meta">
              <span>{{ screenshotSource === 'file' ? 'Załączony plik' : 'Zaznaczony obszar' }}</span>
            </div>
            <!-- .stop on click: Vuetify's v-btn ripple briefly attaches
                 a child element that gets removed before event bubbling
                 finishes. The outside-click document handler's
                 `target.closest('.comparator-feedback__menu')` check
                 then returns null for the now-detached ripple node and
                 mistakenly closes the popover. Stopping propagation
                 keeps the click from reaching the document listener
                 entirely. -->
            <v-btn icon small @click.stop="removeScreenshot" aria-label="Usuń screenshot">
              <v-icon small>mdi-close</v-icon>
            </v-btn>
          </div>

          <!-- Footer: subtle attach icons on the left, action buttons on
               the right. Folding the attach affordances into the same row
               as Anuluj/Wyślij saves vertical space and gives the popover
               a tighter, calmer feel — the previous full-width attach
               buttons were visually competing with the primary submit. -->
          <div class="comparator-feedback__footer">
            <div class="comparator-feedback__attach-icons">
              <button
                type="button"
                class="comparator-feedback__attach-icon"
                :disabled="isCapturingScreenshot"
                aria-label="Zaznacz obszar"
                data-tooltip="Zaznacz obszar"
                @click="startAreaScreenshot"
              >
                <v-icon size="24">mdi-selection-drag</v-icon>
              </button>
              <button
                type="button"
                class="comparator-feedback__attach-icon"
                :disabled="isCapturingScreenshot"
                aria-label="Wgraj plik"
                data-tooltip="Wgraj plik"
                @click="triggerFilePicker"
              >
                <v-icon size="24">mdi-paperclip</v-icon>
              </button>
              <input
                ref="fileInputRef"
                type="file"
                accept="image/*"
                class="comparator-feedback__file-input"
                @change="onFileChange"
              />
            </div>

            <div class="comparator-feedback__actions">
              <fp-btn text height="40px" @click="cancelPopover">Anuluj</fp-btn>
              <!-- `primary` boolean (not color="primary") — FpBtn renders
                   outlined by default and only switches to filled+depressed
                   when one of the semantic flags (primary/success/etc.) is
                   set. Matched height with Anuluj so the action row reads
                   as a balanced pair; the filled primary fill alone is
                   enough to communicate which is the main action. -->
              <fp-btn
                primary
                height="40px"
                :loading="isSubmitting"
                @click="submit"
              >
                Wyślij
              </fp-btn>
            </div>
          </div>
        </template>
      </div>
    </v-menu>

      <!-- Mounted into the document body via Vuetify's default attach;
           lives outside the menu so closing the menu doesn't unmount the
           overlay. -->
      <area-screenshot-overlay
        v-if="isCapturingArea"
        @complete="onAreaComplete"
        @cancel="onAreaCancel"
      />
    </template>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, nextTick, watch, onBeforeUnmount } from 'vue';
import AreaScreenshotOverlay from '@/components/calculator/results/feedback/AreaScreenshotOverlay.vue';
import { captureArea, compressUploadedImage, AreaRect, } from '@/components/calculator/results/feedback/useScreenshotCapture';
import { captureComparatorFeedback, } from '@/plugins/comparatorFeedback';
import { useSimulationStore, } from '@/components/calculator/services/SimulationStore';
import { useInstance, } from '@/composables/useInstance';

type RatingValue = 1 | 2 | 3 | 4 | 5;
type RatingOption = { value: RatingValue; emoji: string; label: string };

// Ordered worst → best so the array index + 1 maps directly to the rating
// value. Labels are surfaced via tooltip/aria for accessibility. Emojis
// render in their native (yellow-faced) colors — earlier we experimented
// with hue-rotate filters to spread them red→green but the recoloring
// pushed faces into off-pink / teal territory, so we keep the defaults.
const RATING_OPTIONS: readonly RatingOption[] = [
  { value: 1, emoji: '😞', label: 'Bardzo źle', },
  { value: 2, emoji: '🙁', label: 'Źle', },
  { value: 3, emoji: '😐', label: 'Średnio', },
  { value: 4, emoji: '🙂', label: 'Dobrze', },
  { value: 5, emoji: '😄', label: 'Świetnie', },
] as const;

// 5 distinct prompts so each rating bucket gets its own contextual ask,
// all phrased neutrally — no `zmienił/-a`-style gendered verb forms.
// Achieved by leaning on infinitive ("co poprawić") or impersonal /
// 2nd-person constructions ("co Cię zniechęca", "Ci się podoba") that
// don't decline by gender.
const POPOVER_QUESTION_BY_RATING: Record<RatingValue, string> = {
  1: 'Co najbardziej Cię zniechęca?',
  2: 'Co możemy poprawić?',
  3: 'Czego brakuje?',
  4: 'Co działa najlepiej?',
  5: 'Co najbardziej Ci się podoba?',
};

const THANKS_DURATION_MS = 3500;
// Shorter grace period after the cursor leaves the hovered thanks popover —
// enough that brief mouse drift doesn't instantly snap it closed, short
// enough that the popover doesn't linger forever.
const THANKS_GRACE_MS = 800;
const MENU_CLOSE_ANIMATION_MS = 200;
const PROMPT_DISMISSED_STORAGE_KEY = 'comparator-feedback-prompt-dismissed-v1';
const WIDGET_HIDDEN_STORAGE_KEY = 'comparator-feedback-widget-hidden-v1';

// Probe localStorage once at module load; sandboxed iframes / Safari private
// mode throw on access, in which case we just treat flags as not-set
// (worst case: bubble/widget shows, which is the intended default anyway).
const readStorageFlag = (key: string): boolean => {
  try {
    return window.localStorage.getItem(key) === 'true';
  } catch {
    return false;
  }
};
const writeStorageFlag = (key: string) => {
  try {
    window.localStorage.setItem(key, 'true');
  } catch {
    /* ignore — sandbox / private mode */
  }
};

export default defineComponent({
  name: 'ComparatorFeedback',
  components: { AreaScreenshotOverlay, },
  setup() {
    const store = useSimulationStore();
    const { $snackbarService, $route, } = useInstance();

    const selectedRating = ref<RatingValue | null>(null);
    const isExpanded = ref<boolean>(true);
    const isMenuOpen = ref<boolean>(false);
    const comment = ref<string>('');
    const screenshotDataUrl = ref<string | null>(null);
    // Where the current screenshot came from — toggles the preview's
    // label between "Zaznaczony obszar" (drag-rect) and "Załączony plik"
    // (uploaded from disk). Null when no screenshot is attached.
    const screenshotSource = ref<'area' | 'file' | null>(null);
    const isCapturingArea = ref<boolean>(false);
    const isCapturingScreenshot = ref<boolean>(false);
    const isSubmitting = ref<boolean>(false);
    const showThanks = ref<boolean>(false);
    const fileInputRef = ref<HTMLInputElement | null>(null);
    // Vuetify's v-textarea is a complex component instance with a `.focus()`
    // method — typed as `any` because Vuetify 2's public types don't model
    // the exposed instance API.
    const textareaRef = ref<any>(null);
    const widgetRootRef = ref<HTMLElement | null>(null);
    const isPromptVisible = ref<boolean>(!readStorageFlag(PROMPT_DISMISSED_STORAGE_KEY));
    const isWidgetHidden = ref<boolean>(readStorageFlag(WIDGET_HIDDEN_STORAGE_KEY));

    const selectedOption = computed<RatingOption | null>(() => {
      if (selectedRating.value === null) return null;
      return RATING_OPTIONS.find(o => o.value === selectedRating.value) ?? null;
    });

    // True when the widget should hide the non-selected emojis in the
    // scale — only after the user has actually rated. Drives the
    // animated "rolling out from the right" expand-on-hover effect by
    // toggling the `--hidden` modifier on each non-selected emoji.
    const isInCollapsedMode = computed<boolean>(() => {
      return selectedRating.value !== null && !isExpanded.value;
    });

    const popoverQuestion = computed<string>(() => {
      return selectedRating.value !== null ? POPOVER_QUESTION_BY_RATING[selectedRating.value] : '';
    });

    /**
     * Picking an emoji highlights it in the still-expanded scale and opens
     * the popover. We keep the scale visible (rather than collapsing to
     * the chosen emoji) so the user can switch their mind by clicking a
     * different emoji while the popover is open — the question and
     * placeholder update to match the new rating bucket. The scale only
     * collapses once feedback is actually submitted (see submit() below).
     */
    const onRatingClick = (value: RatingValue) => {
      selectedRating.value = value;
      // nextTick so the trigger DOM has updated (selected-state class on
      // the active emoji) before v-menu measures the activator — keeps
      // the popover position consistent across rapid re-picks.
      nextTick(() => {
        isMenuOpen.value = true;
      });
    };

    // No-op: kept for future hooks (analytics on menu close, etc). v-menu's
    // v-model already takes care of state propagation.
    // Manual outside-click handler. We disabled v-menu's built-in
    // `close-on-click` because it runs in the document's *capture* phase
    // (Vuetify's `v-click-outside` directive), which fires before our
    // emoji buttons' `@click.stop` and was eating mid-popover rating
    // switches. Our replacement listens at *bubble* phase, so a stopped
    // emoji click never reaches it — but a plain click anywhere else
    // (the calculator content, the zoom toggle, etc.) does, and closes
    // the popover.
    const onDocumentClick = (event: MouseEvent) => {
      if (!isMenuOpen.value) return;
      const target = event.target as Element | null;
      if (!target) return;
      // Click inside the trigger / scale / 3-dots strip lives inside
      // the widget root, which we have a ref on.
      if (widgetRootRef.value?.contains(target)) return;
      // Click inside the teleported popover content — Vuetify mounts it
      // under <body>, so `widgetRootRef.contains` misses it.
      if (target.closest?.('.comparator-feedback__menu')) return;
      // Click inside the teleported overflow (3-dots) menu — also
      // hoisted out of the widget root by Vuetify's menu.
      if (target.closest?.('.comparator-feedback__overflow-menu')) return;
      isMenuOpen.value = false;
    };

    watch(isMenuOpen, open => {
      if (open) {
        // Bubble phase (default) so the emoji buttons' @click.stop keeps
        // mid-popover rating switches from reaching this handler.
        document.addEventListener('click', onDocumentClick);
      } else {
        document.removeEventListener('click', onDocumentClick);
        // Collapse the scale once the popover closes after a rating
        // already exists. Without this, the hover-expanded scale stays
        // stuck open if the user moved the cursor away while the menu
        // was up (onTriggerLeave short-circuits when isMenuOpen is
        // true) and then closed the popover via Anuluj or outside-click.
        if (selectedRating.value !== null) {
          isExpanded.value = false;
        }
      }
    });

    onBeforeUnmount(() => {
      document.removeEventListener('click', onDocumentClick);
      clearThanksTimer();
    });

    const cancelPopover = () => {
      isMenuOpen.value = false;
    };

    // Single source of truth for clearing the attached screenshot — keeps
    // `screenshotDataUrl` and `screenshotSource` in sync so neither side
    // can drift if a future code path forgets to null one of them.
    const clearAttachment = () => {
      screenshotDataUrl.value = null;
      screenshotSource.value = null;
    };

    const removeScreenshot = () => {
      clearAttachment();
    };

    const dismissPrompt = () => {
      isPromptVisible.value = false;
      writeStorageFlag(PROMPT_DISMISSED_STORAGE_KEY);
    };

    const dismissWidget = () => {
      isWidgetHidden.value = true;
      writeStorageFlag(WIDGET_HIDDEN_STORAGE_KEY);
    };

    const restoreWidget = () => {
      isWidgetHidden.value = false;
      // Drop the persisted "hidden" flag so the choice doesn't survive
      // the next page load against the user's expressed intent. Wrapped
      // in try/catch for the same reasons readStorageFlag is.
      try {
        window.localStorage.removeItem(WIDGET_HIDDEN_STORAGE_KEY);
      } catch {
        /* ignore */
      }
    };

    // Hover-driven re-expand for the post-submission collapsed state.
    // Only active when the user has already rated (selectedRating set)
    // AND the scale is currently collapsed — in the fresh, never-rated
    // state the scale is already expanded by default, so the handlers
    // become no-ops and don't interfere with first-time picking.
    const onTriggerEnter = () => {
      if (selectedRating.value === null) return;
      if (!isExpanded.value) {
        isExpanded.value = true;
      }
    };
    const onTriggerLeave = () => {
      if (selectedRating.value === null) return;
      // Keep the scale open while the popover is up — the user is mid-
      // interaction and the cursor has just moved from the scale to the
      // popover content (a natural mouseleave event that shouldn't
      // collapse anything).
      if (isMenuOpen.value) return;
      isExpanded.value = false;
    };

    // Focus the comment textarea each time the popover opens (and when its
    // contents flip back from the thanks view to the form). We delay one
    // frame past the menu transition so v-textarea has finished mounting —
    // calling .focus() before the input element exists is a silent no-op.
    watch(
      [isMenuOpen, showThanks],
      async ([open, thanks]) => {
        if (!open || thanks) return;
        await nextTick();
        await new Promise<void>(resolve => setTimeout(resolve, 80));
        const instance = textareaRef.value;
        // Vuetify exposes `.focus()` on the public instance; some refs in
        // tests fall through to the underlying <textarea>, so guard both.
        if (instance?.focus) {
          instance.focus();
        } else if (instance?.$refs?.input?.focus) {
          instance.$refs.input.focus();
        }
      },
    );

    const startAreaScreenshot = () => {
      // Keep the popover open and just push the overlay on top of it.
      // The overlay is z-index 10000 (vs ~7 on v-menu content) so it
      // dims the whole page including the popover — which means we
      // don't need a close→reopen dance after the user finishes drag.
      // The popover stays mounted, so when the overlay unmounts on
      // mouseup the popover is *instantly* visible at full layout (no
      // slide-y transition lag). modern-screenshot's `filter` callback
      // drops the popover from the captured pixels regardless.
      isCapturingArea.value = true;
    };

    const onAreaComplete = async (rect: AreaRect) => {
      isCapturingArea.value = false;
      isCapturingScreenshot.value = true;
      // No isMenuOpen toggle here — the popover stayed open under the
      // overlay (see startAreaScreenshot), so unmounting the overlay
      // alone reveals it instantly. The spinner attachment row renders
      // on this same tick.
      //
      // We still wait two paint frames before kicking captureArea so
      // the spinner has a chance to render: modern-screenshot stalls
      // the main thread synchronously and would otherwise consume the
      // paint budget intended for the spinner.
      await nextTick();
      await new Promise<void>(resolve => requestAnimationFrame(() => resolve()));
      await new Promise<void>(resolve => requestAnimationFrame(() => resolve()));
      try {
        const dataUrl = await captureArea(rect);
        if (dataUrl) {
          screenshotDataUrl.value = dataUrl;
          screenshotSource.value = 'area';
        } else {
          $snackbarService.openErrorSnackbar('Nie udało się zrobić screenshota. Spróbuj jeszcze raz lub wgraj plik.');
        }
      } finally {
        isCapturingScreenshot.value = false;
      }
    };

    const onAreaCancel = () => {
      // Popover stayed open under the overlay — just unmounting the
      // overlay is enough to reveal it again.
      isCapturingArea.value = false;
    };

    const triggerFilePicker = () => {
      fileInputRef.value?.click();
    };

    const onFileChange = async (event: Event) => {
      const input = event.target as HTMLInputElement;
      const file = input.files?.[0];
      // Reset the input so the same file can be picked again after removal —
      // change events don't fire for an unchanged value otherwise.
      input.value = '';
      if (!file) return;
      // Generous raw-size guard. We always re-encode to WebP @ 0.7 / 1280 px
      // below, so the bytes that hit the event payload are bounded; this cap
      // just stops decoding absurdly large files (10MB+ phone shots).
      const MAX_BYTES = 10 * 1024 * 1024;
      if (file.size > MAX_BYTES) {
        $snackbarService.openErrorSnackbar('Plik większy niż 10 MB — wybierz mniejszy obrazek.');
        return;
      }
      isCapturingScreenshot.value = true;
      try {
        const dataUrl = await compressUploadedImage(file);
        if (dataUrl) {
          screenshotDataUrl.value = dataUrl;
          screenshotSource.value = 'file';
        } else {
          $snackbarService.openErrorSnackbar('Nie udało się wczytać pliku. Spróbuj innego obrazka.');
        }
      } finally {
        isCapturingScreenshot.value = false;
      }
    };

    // Partial reset run after the "Dziękujemy" popover closes. Keeps the
    // user's chosen rating + the collapsed state so the toolbar ends up
    // showing "you rated 🙂" — they can click that emoji to re-expand and
    // submit a different rating later. Only the per-submission form
    // contents (comment / screenshot / thanks flag) are cleared so the
    // next popover opens fresh.
    const resetAfterSubmit = () => {
      comment.value = '';
      clearAttachment();
      showThanks.value = false;
    };

    // Timer bookkeeping for the post-submit "Dziękujemy" auto-close. We
    // keep the handle in scope so hovering the popover can cancel the
    // pending close, and mouseleave can re-arm a shorter grace timer.
    let thanksCloseTimer: ReturnType<typeof setTimeout> | null = null;
    const clearThanksTimer = () => {
      if (thanksCloseTimer !== null) {
        clearTimeout(thanksCloseTimer);
        thanksCloseTimer = null;
      }
    };
    const scheduleThanksClose = (delayMs: number) => {
      clearThanksTimer();
      thanksCloseTimer = setTimeout(() => {
        thanksCloseTimer = null;
        isMenuOpen.value = false;
        setTimeout(resetAfterSubmit, MENU_CLOSE_ANIMATION_MS);
      }, delayMs);
    };
    const onThanksMouseEnter = () => {
      // Cursor is on the popover — the user is reading or about to click
      // an action. Cancel the auto-close until they leave.
      clearThanksTimer();
    };
    const onThanksMouseLeave = () => {
      // After the cursor leaves, give a short window before sliding the
      // popover out so brief drift / accidental exits don't snap it.
      if (showThanks.value) {
        scheduleThanksClose(THANKS_GRACE_MS);
      }
    };
    const closeThanksNow = () => {
      clearThanksTimer();
      isMenuOpen.value = false;
      setTimeout(resetAfterSubmit, MENU_CLOSE_ANIMATION_MS);
    };
    // Triggered from the small "Ukryj widget" button in the thanks
    // state. Hides the widget AND tears down the open popover at once
    // (the menu's activator about to vanish — letting v-menu close
    // itself usually works but doing it explicitly avoids a flicker
    // frame where the popover is rendered with no anchor).
    const dismissWidgetFromThanks = () => {
      clearThanksTimer();
      isMenuOpen.value = false;
      dismissWidget();
      // Reset the form state now that the user has both submitted and
      // dismissed; the next time they click "Pokaż widget" they should
      // get a fresh scale, not a stuck thanks view.
      showThanks.value = false;
      comment.value = '';
      clearAttachment();
    };

    const submit = async () => {
      if (selectedRating.value === null) return;
      isSubmitting.value = true;
      try {
        await captureComparatorFeedback({
          rating: selectedRating.value,
          comment: comment.value,
          screenshot: screenshotDataUrl.value,
          productType: store.productType ? String(store.productType) : null,
          simulationId: store.simulationId ?? null,
          routeName: $route.value?.name?.toString() ?? null,
        });
      } finally {
        isSubmitting.value = false;
      }
      // Dismiss the "Jak oceniasz porównywarkę?" prompt — the user has
      // answered it, so showing it again on later visits would feel
      // redundant. Persisted to localStorage by dismissPrompt() so the
      // bubble doesn't re-appear on the next page load either.
      if (isPromptVisible.value) {
        dismissPrompt();
      }
      // Collapse the scale to the chosen emoji as a visual confirmation
      // that the submission has landed (the popover at this point swaps
      // to "Dziękujemy"). The collapsed state PERSISTS after the menu
      // closes — the user keeps seeing their last rating until they click
      // the collapsed emoji to re-expand the scale and rate again.
      isExpanded.value = false;
      showThanks.value = true;
      // Hand off to the hover-aware scheduler so cursor entering the
      // thanks popover pauses the timer (and leaving re-arms it with
      // a shorter grace).
      scheduleThanksClose(THANKS_DURATION_MS);
    };

    return {
      RATING_OPTIONS,
      selectedRating,
      selectedOption,
      isExpanded,
      isInCollapsedMode,
      isMenuOpen,
      comment,
      screenshotDataUrl,
      screenshotSource,
      isCapturingArea,
      isCapturingScreenshot,
      isSubmitting,
      showThanks,
      fileInputRef,
      textareaRef,
      widgetRootRef,
      isPromptVisible,
      isWidgetHidden,
      popoverQuestion,
      onRatingClick,
      onTriggerEnter,
      onTriggerLeave,
      onThanksMouseEnter,
      onThanksMouseLeave,
      closeThanksNow,
      dismissWidgetFromThanks,
      cancelPopover,
      removeScreenshot,
      dismissPrompt,
      dismissWidget,
      restoreWidget,
      startAreaScreenshot,
      onAreaComplete,
      onAreaCancel,
      triggerFilePicker,
      onFileChange,
      submit,
    };
  },
});
</script>

<style scoped lang="scss">
.comparator-feedback {
  display: inline-flex;
  align-items: center;
  gap: 10px;

  // Minimized state — the widget has been hidden by the user and only
  // the 3-dots reentry point remains. Same height as the full widget so
  // toggling between hidden/visible doesn't shift the surrounding
  // toolbar layout.
  &--minimized {
    height: 40px;
  }

  // The trigger is the visible "pill" — background, border, height,
  // padding all live here so the emoji scale, the collapsed single emoji,
  // and the 3-dots overflow button all sit inside one shared container.
  // Height matches the zoom toggle (S/M/L/XL) next to it in the toolbar
  // so the two controls line up horizontally; vertical padding is the
  // small slack between the 30 px inner buttons and the 40 px outer
  // pill, side padding gives the first/last emoji visible breathing
  // room from the pill's rounded edges.
  &__trigger {
    display: inline-flex;
    align-items: center;
    background-color: var(--token-core-surface, #f5f5f5);
    border: 1px solid rgba(0, 0, 0, 0.08);
    border-radius: 10px;
    height: 40px;
    // Uniform 4 px padding — the previous 6 px left/right made the
    // collapsed state (single highlighted emoji + divider + dots) feel
    // visually unbalanced because the selected-state background on the
    // emoji button already provides its own visual inset from the pill
    // edge.
    padding: 4px;
    // Pair with the per-emoji width transition below so the pill itself
    // grows/shrinks smoothly as the hidden emojis slide in from the
    // left. Without this the inner content animates but the pill snaps
    // to the new width on the next frame.
    transition: padding 0.22s ease-out;
  }

  // Vertical separator between the scale/collapsed section and the
  // 3-dots overflow button — proportionally a touch shorter than the
  // pill's inner height so it reads as a hairline rather than a hard
  // partition.
  &__trigger-divider {
    width: 1px;
    height: 24px;
    background-color: rgba(0, 0, 0, 0.12);
    margin: 0 4px;
    flex-shrink: 0;
  }

  // Speech-bubble prompt anchored to the left of the scale. The :after
  // pseudo-element draws a small triangle on its right edge so the bubble
  // visually "points" at the emojis. The navy comes from Vuetify's
  // secondary theme color (#172c57 in EnvironmentTheme.ts), exposed as a
  // CSS var; the fallback covers the rare case the var isn't resolved
  // yet during initial paint.
  &__prompt {
    --prompt-bg: var(--v-secondary-base, #172c57);
    position: relative;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    background-color: var(--prompt-bg);
    color: #ffffff;
    padding: 8px 6px 8px 14px;
    // Gently rounded — not a full pill, not a hard rectangle. 10px reads
    // as "speech bubble" without competing with the round emoji buttons.
    border-radius: 10px;
    font-size: 13px;
    font-weight: 500;
    line-height: 1;
    white-space: nowrap;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);

    &::after {
      content: '';
      position: absolute;
      right: -6px;
      top: 50%;
      transform: translateY(-50%);
      width: 0;
      height: 0;
      border-left: 6px solid var(--prompt-bg);
      border-top: 6px solid transparent;
      border-bottom: 6px solid transparent;
    }
  }

  &__prompt-text {
    padding-right: 2px;
  }

  &__prompt-close {
    appearance: none;
    background: transparent;
    border: 0;
    padding: 0;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: #ffffff;
    transition: background-color 0.12s ease;

    &:hover {
      background-color: rgba(255, 255, 255, 0.15);
    }

    ::v-deep .v-icon {
      font-size: 16px !important;
      color: inherit;
    }
  }

  // Inner emoji group — transparent now (the pill lives on .__trigger).
  // Just a flex row of buttons.
  &__scale {
    display: inline-flex;
    align-items: center;
  }

  // Suppress per-emoji tooltips while the popover is open. Keeping them
  // around clutters the popover's anchor area and the just-picked emoji
  // can end up sitting under the user's cursor with its label hanging
  // half-into the open popover, which reads as a glitch.
  // Modifier moved to .__trigger so the suppression applies even though
  // the inner .__scale element no longer carries the pill class.
  &__trigger--menu-open .comparator-feedback__emoji {
    &:hover::after,
    &:hover::before {
      display: none;
    }
  }

  &__emoji {
    appearance: none;
    background: transparent;
    border: 0;
    padding: 0;
    width: 30px;
    height: 30px;
    // Soft-rounded square matches the parent container's 10px radius
    // proportionally — full circles inside a rounded-rectangle container
    // create visual tension (round-in-square mismatch).
    border-radius: 6px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    position: relative; // anchor for ::after tooltip
    flex-shrink: 0; // don't let flex squish the visible buttons
    // No overflow:hidden on the button itself — the ::after tooltip
    // sits inside the button's box and would get clipped. We delegate
    // the glyph-clipping to a child wrapper (`__emoji-inner`).
    // Width + opacity animate when a button toggles between visible and
    // `--hidden` (collapsed mode on/off); background animates on hover.
    transition:
      width 0.22s ease-out,
      opacity 0.18s ease-out,
      background-color 0.12s ease;

    &:hover {
      background-color: rgba(0, 0, 0, 0.06);
    }

    // Hover-scale is on the inner glyph (not the whole button) so the
    // ::after tooltip doesn't grow with the emoji.
    &:hover .comparator-feedback__emoji-glyph {
      transform: scale(1.35);
    }

    &--selected {
      background-color: rgba(0, 0, 0, 0.12);
    }

    // Collapsed-mode modifier: non-selected emojis fold to zero width
    // and fade out. Since DOM order is preserved [😞 🙁 😐 🙂 😄], a
    // selected emoji at position 4 ends up rendered first visually
    // (the zero-width siblings before it take no space) — when the
    // user hovers and the widget re-expands, the hidden emojis slide
    // in to the LEFT of the previously-visible one, giving the
    // "rolling out leftward" feel.
    &--hidden {
      width: 0;
      opacity: 0;
      pointer-events: none;
    }

    // While the popover is open the per-emoji tooltips are suppressed
    // (see .comparator-feedback__scale--menu-open below): the user has
    // already picked, so the labels become noise and the popover already
    // explains the chosen rating's context.

    // Instant tooltip (native `title` would have a ~500 ms browser delay).
    // Shown on :hover so it's always discoverable, even while another
    // emoji is the current selection or the popover is open below.
    &:hover::after {
      content: attr(data-tooltip);
      position: absolute;
      top: calc(100% + 8px);
      left: 50%;
      transform: translateX(-50%);
      background-color: rgba(33, 33, 33, 0.92);
      color: #ffffff;
      padding: 5px 9px;
      border-radius: 4px;
      font-size: 12px;
      font-weight: 500;
      line-height: 1.2;
      white-space: nowrap;
      pointer-events: none;
      z-index: 50;
    }

    // Small arrow on the tooltip pointing up at the emoji. Stacked above
    // the ::after on its own z-index plane so they don't seam.
    &:hover::before {
      content: '';
      position: absolute;
      top: calc(100% + 2px);
      left: 50%;
      transform: translateX(-50%);
      border: 4px solid transparent;
      border-bottom-color: rgba(33, 33, 33, 0.92);
      pointer-events: none;
      z-index: 50;
    }
  }

  // Clip wrapper between the button and the glyph. The button stays
  // overflow: visible (so its ::after tooltip can escape its bounds);
  // this wrapper does the actual glyph clipping while the button
  // shrinks toward width: 0 in collapsed mode.
  &__emoji-inner {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
  }

  &__emoji-glyph {
    // Slight font-size bump so the emojis read clearly inside the 30px button.
    // Apple Color Emoji / Segoe UI Emoji render predictably without an
    // explicit font-family declaration — relying on system defaults.
    font-size: 18px;
    line-height: 1;
    transition: transform 0.12s ease;
    display: inline-block; // so transform works
  }

  // Overflow (3-dots) button — sits inside the trigger pill alongside
  // the emoji scale, sized to match the emoji buttons (30 × 30 with a
  // 6 px radius).
  //
  // The --standalone modifier is for the minimized state where the
  // widget has been hidden and only this button remains: it picks up
  // the trigger pill's own bg + border + height so the entry point
  // looks like a self-contained chip rather than a bare icon.
  &__overflow-btn {
    appearance: none;
    background: transparent;
    border: 0;
    width: 30px;
    height: 30px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 6px;
    color: rgba(0, 0, 0, 0.55);
    cursor: pointer;
    transition: background-color 0.12s ease, color 0.12s ease, border-color 0.12s ease;

    &:hover {
      background-color: rgba(0, 0, 0, 0.06);
      color: rgba(0, 0, 0, 0.78);
    }

    &--standalone {
      // Match the trigger pill's exterior so the minimized button sits
      // in the toolbar at the same height/radius as the full widget
      // (and the zoom toggle next to it).
      width: 40px;
      height: 40px;
      border-radius: 10px;
      background-color: var(--token-primary-surface, #f5f5f5);
      border: 1px solid rgba(0, 0, 0, 0.08);

      &:hover {
        background-color: rgba(0, 0, 0, 0.04);
        border-color: rgba(0, 0, 0, 0.14);
      }
    }
  }

  &__popover {
    background-color: var(--token-primary-surface, #ffffff);
    // Match the menu wrapper's border-radius so the white background is
    // shaped the same way. Since we set overflow: visible on the wrapper
    // (so tooltips don't clip), this radius is what actually shapes the
    // visible corners — without it the wrapper's shadow would round but
    // the white inside would render square.
    border-radius: 18px;
    padding: 16px 20px;
    // 340–400 px range. Wide enough that the attach-icon tooltips
    // ("Zaznacz obszar" being the longest) stay visible without
    // per-icon offset math — they extend past the popover's left
    // edge but render fine thanks to the menu wrapper's
    // `overflow: visible !important`. The thanks state has shorter
    // content so it settles closer to the min.
    min-width: 340px;
    max-width: 400px;
  }

  &__popover-header {
    display: flex;
    // Align items to the emoji's vertical center, but use the text column's
    // own intrinsic baseline so overscript + question read like a tight
    // two-line stack next to a larger glyph.
    align-items: center;
    gap: 10px;
    margin-bottom: 12px;
    font-size: 14px;
  }

  &__popover-emoji {
    font-size: 26px;
    line-height: 1;
    flex-shrink: 0;
  }

  &__popover-header-text {
    display: flex;
    flex-direction: column;
    // Tight gap so the overscript hugs the question — they read as one
    // labelled block rather than two separate lines.
    gap: 2px;
    min-width: 0;
  }

  &__popover-overscript {
    // Small uppercase muted label sitting above the question. Uppercase +
    // letter-spacing makes a short word ("Źle", "Dobrze") look intentional
    // rather than just a tiny line of text.
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: rgba(0, 0, 0, 0.55);
    line-height: 1;
  }

  &__popover-question {
    font-size: 14px;
    line-height: 1.25;
  }

  // Wrapper div around v-textarea — carries the bottom spacer to the
  // footer. Putting margin on the v-input itself was unreliable: Vuetify's
  // .v-text-field has its own padding-top: 12px / margin-top: 4px and a
  // higher-specificity selector that ate into our scoped class.
  &__textarea-wrap {
    margin-bottom: 16px;
  }

  &__attachment {
    display: flex;
    align-items: center;
    gap: 10px;
    background-color: rgba(0, 0, 0, 0.04);
    border-radius: 6px;
    padding: 8px 10px;
    margin-bottom: 12px;

    // Loading variant — same shape, just a spinner where the thumb
    // would be and slightly more vertical breathing room since
    // there's no image.
    &--loading {
      padding: 12px;
      color: rgba(0, 0, 0, 0.6);
    }
  }

  // Plain CSS spinner — 18 px ring with the primary color spinning
  // around it. Used in place of Vuetify's v-progress-circular which
  // didn't render reliably at this size.
  &__spinner {
    flex-shrink: 0;
    width: 18px;
    height: 18px;
    border: 2px solid rgba(0, 0, 0, 0.12);
    border-top-color: var(--token-primary-base, #1976d2);
    border-radius: 50%;
    animation: comparator-feedback-spin 0.8s linear infinite;
  }

  &__attachment-thumb {
    width: 48px;
    height: 32px;
    object-fit: cover;
    border-radius: 4px;
    border: 1px solid rgba(0, 0, 0, 0.08);
    flex-shrink: 0;
  }

  &__attachment-meta {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    flex: 1;
    min-width: 0;
  }

  // Footer row: attach-icons on the left, action buttons on the right.
  // `justify-content: space-between` pushes them to opposite edges; if the
  // popover ever gets narrower than their combined width, the icons stay
  // on the left and actions wrap underneath (acceptable degradation).
  &__footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
  }

  // Subtle icon-only attach buttons. No background by default — just an
  // icon. Hover/active states give a soft tint so they read as actionable.
  &__attach-icons {
    display: flex;
    align-items: center;
    gap: 2px;
  }

  &__attach-icon {
    appearance: none;
    background: transparent;
    border: 0;
    // Tap target sized for a 24 px icon with 8 px slack on each side.
    width: 40px;
    height: 40px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    color: rgba(0, 0, 0, 0.55);
    cursor: pointer;
    position: relative; // anchor for ::after tooltip
    transition: background-color 0.12s ease, color 0.12s ease;

    &:hover:not(:disabled) {
      background-color: rgba(0, 0, 0, 0.06);
      color: rgba(0, 0, 0, 0.78);
    }

    &:active:not(:disabled) {
      background-color: rgba(0, 0, 0, 0.1);
    }

    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }

    // Instant CSS tooltip — the tooltip BODY is left-anchored so it
    // extends rightward (keeps the leftmost button's label from
    // clipping against the popover's left edge), but the arrow/tail
    // is independently centered on the icon so it still visually
    // points at the right element.
    &:hover:not(:disabled)::after {
      content: attr(data-tooltip);
      position: absolute;
      bottom: calc(100% + 6px);
      left: 0;
      background-color: rgba(33, 33, 33, 0.92);
      color: #ffffff;
      padding: 5px 9px;
      border-radius: 4px;
      font-size: 12px;
      font-weight: 500;
      line-height: 1.2;
      white-space: nowrap;
      pointer-events: none;
      z-index: 50;
    }

    &:hover:not(:disabled)::before {
      content: '';
      position: absolute;
      bottom: calc(100% + 2px);
      left: 50%;
      transform: translateX(-50%);
      border: 4px solid transparent;
      border-top-color: rgba(33, 33, 33, 0.92);
      pointer-events: none;
      z-index: 50;
    }
  }

  &__file-input {
    display: none;
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__thanks {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 4px 0;
  }

  // Header row: emoji + thanks copy + close X. Same layout as before,
  // just lifted into its own row so the "Ukryj widget" affordance can
  // sit underneath without crowding the message.
  &__thanks-row {
    display: flex;
    align-items: flex-start;
    gap: 12px;
  }

  // Larger "celebratory" emoji with a green check badge — anchors the
  // thanks message and acknowledges which rating just went through.
  &__thanks-emoji {
    position: relative;
    width: 56px;
    height: 56px;
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  &__thanks-emoji-glyph {
    font-size: 48px;
    line-height: 1;
  }

  // Bigger green check badge proportional to the bumped emoji. 3 px
  // white border keeps it cleanly separated from the emoji's edge.
  &__thanks-emoji-check {
    position: absolute;
    bottom: -2px;
    right: -2px;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background-color: var(--token-success-base, #55F68B);
    border: 3px solid var(--token-primary-surface, #ffffff);
    display: inline-flex;
    align-items: center;
    justify-content: center;

    ::v-deep .v-icon {
      font-size: 16px !important;
    }
  }

  // Bumped heading + caption sizes so the thanks card reads as the
  // main moment, not just a passing toast.
  &__thanks-heading {
    font-size: 16px;
    line-height: 1.25;
  }

  &__thanks-caption {
    font-size: 14px;
    line-height: 1.4;
    color: rgba(0, 0, 0, 0.62);
  }

  &__thanks-text {
    display: flex;
    flex-direction: column;
    gap: 2px;
    flex: 1;
    min-width: 0;
  }

  // Close X — small, subtle, top-right of the message. Auto-aligns
  // to the top via the parent's `align-items: flex-start`.
  &__thanks-close {
    appearance: none;
    background: transparent;
    border: 0;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: rgba(0, 0, 0, 0.45);
    cursor: pointer;
    flex-shrink: 0;
    transition: background-color 0.12s ease, color 0.12s ease;

    &:hover {
      background-color: rgba(0, 0, 0, 0.06);
      color: rgba(0, 0, 0, 0.72);
    }
  }

  // Action row under the thanks message. Single right-aligned subtle
  // text-button — visible enough to discover, quiet enough not to
  // compete with the green checkmark + heading above.
  &__thanks-actions {
    display: flex;
    justify-content: flex-end;
  }

  // (No custom hide-btn rules — fp-btn renders the secondary action
  // with the design system's own outlined styling.)
}

// Prompt enter/leave: a subtle slide-from-right + fade so the bubble doesn't
// pop in/out abruptly. Lives outside the BEM block so Vue's transition class
// names compose correctly (`<name>-enter`, `<name>-leave-to`).
.comparator-feedback-prompt-fade-enter-active,
.comparator-feedback-prompt-fade-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}
.comparator-feedback-prompt-fade-enter,
.comparator-feedback-prompt-fade-leave-to {
  opacity: 0;
  transform: translateX(4px);
}

// Keyframes for the screenshot-loading spinner. Declared at the root
// level (not nested inside any selector) — @keyframes is global by
// scope; nesting it inside a BEM block in Vue's scoped SCSS would not
// generate a usable animation name.
@keyframes comparator-feedback-spin {
  to { transform: rotate(360deg); }
}
</style>

<style lang="scss">
// The popover content is teleported to <body> by Vuetify so its styles
// can't be scoped — using a content-class instead. Vuetify's own
// .v-menu__content rule sets `overflow-y: auto` on the wrapper, which
// also clips horizontal overflow in most browsers — exactly what was
// chopping the attach-icon tooltips on their left edge. Force the
// override with `!important` (same-specificity rule from Vuetify wins
// otherwise on style-tag order). Bigger border-radius for a softer
// look, matched on the inner .__popover so the white background
// reads the same.
.comparator-feedback__menu {
  overflow: visible !important;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.18) !important;
  border-radius: 18px !important;
}

// Overflow (3-dots) menu list. Vuetify's default `dense` icon gutter
// (32 px+) is built for full-page nav menus and feels oversized in a
// 2-option toolbar overflow; tightening to 12 px lines the icon and
// label up like a compact dropdown.
.comparator-feedback__overflow-menu {
  .v-list-item__icon {
    margin-right: 12px !important;
    min-width: 0 !important;
  }
}
</style>
